import { StatusActivity } from "utils/activity.util";
import { getLongDate, getShortDate, getWeek } from "utils/date.utils";

export const courseTypes = ['COURSE_ACTIVITY', 'COURSE_GRAIN'];

export interface ApiCourseSessionType {
    id: string;
    completion_rate: number;
    course: {
        id: string;
        name: string;
        description: string;
        teaching_area: string;
        domains: string[];
        thumbnail: string;
        introduction_video_url: string;
        introduction_video_provider: string;
        stages: stageType[]
    };
    activity_sessions:
    {
        id: string;
        activity_id: string;
        state: string
    }[];
    title: string;
    student_group: string;
}
export interface ApiCourseDetailsType {
    id: string;
    title?: string;
    description?: string;
    name: string;
    thumbnail: string;
    stages: stageType[];
}

export interface courseType {
    id: string;
    type?: string;
    title: string;
    description?: string;
    imageSrc: string;
    progress: number;
    offline?: boolean;
    status?: string;
    date?: string;
    longDate?: string;
    stages?: stageType[];
    sessionId?: string;
    collectionName?: string;
    sessionCreatedBy?: string;
    week?: { weekNumber: number, label: { firstDayWeek: string, lastDayWeek: string } };
}

export interface stageType {
    activities: {
        id: string;
        courseSessionId: string;
        name: string;
        thumbnail: string;
        elements: number;
        status: string;
        mandatory: boolean;
        order: number;
        sessionCreatedBy: string;
    }[];
    expected_completion_rate: number;
    grains: {
        id: string;
        courseSessionId: string;
        name: string;
        thumbnail: string;
        order: number
    }[];
    locked?: boolean;
    name: string;
    stageOrder: number;
}

export interface ApiCourseType {

    id: string;
    course: {
        id: string;
        name: string;
        description: string;
        domains: string[];
        thumbnail: string;
        can_be_collaborative: boolean
    },
    course_session: {
        id: string;
        start_date: string;
        end_date: string;
        name: string;
        last_update: string;
        owner_name: string | null;
    },
    completion_rate: number
}
/**
 * @class Defines an authentification object.
 */
export class CourseModel {
    id: string;
    type: string;
    title: string;
    description: string;
    imageSrc: string;
    progress: number;
    offline: boolean;
    status: string;
    rawDate: string;
    sessionCreatedBy: string;

    /**
     * @constructor Creates an instance of an authentification object built from an original data from the API.
     * @param {bo} bo is the basic object come from API.
     */
    constructor(bo: ApiCourseType) {
        this.id = bo.id;
        this.type = "COURSE";
        this.title = bo.course.name;
        this.description = bo.course.description;
        this.imageSrc = bo.course.thumbnail;
        this.progress = Math.round(bo.completion_rate * 100);
        this.offline = false;
        this.status = (bo.completion_rate === 1)
            ? StatusActivity.FINISHED
            : (bo.completion_rate > 0 ? StatusActivity.PENDING : StatusActivity.NOT_STARTED);
        this.rawDate = bo.course_session.end_date;
        this.sessionCreatedBy = bo.course_session.owner_name ?? "";
    }

    get week() {
        return getWeek(this.rawDate);
    }

    get date() {
        return getShortDate(this.rawDate);
    }

    get longDate() {
        return getLongDate(this.rawDate);
    }
}

export class CourseSessionModel {
    id: string;
    title: string;
    description: string;
    imageSrc: string;
    stages: stageType[];
    progress: number;

    /**
     * @constructor Creates an instance of a CourseSessionModel built from an API response.
     * @param {ApiCourseSessionType} bo - object coming from the API.
     */

    private constructor(params: {
        id: string;
        title: string;
        description: string;
        imageSrc: string;
        stages: stageType[];
        progress: number;
    }) {
        this.id = params.id;
        this.title = params.title;
        this.description = params.description;
        this.imageSrc = params.imageSrc;
        this.stages = params.stages;
        this.progress = params.progress;
    }

    private static hasRootStages(
        bo: ApiCourseSessionType | ApiCourseDetailsType
    ): bo is ApiCourseDetailsType {
        return "stages" in bo && Array.isArray((bo as any).stages);
    }

    private static hasCourse(
        bo: ApiCourseSessionType | ApiCourseDetailsType
    ): bo is ApiCourseSessionType {
        return "course" in bo && (bo as any).course != null;
    }

    static fromApi(bo: ApiCourseSessionType | ApiCourseDetailsType): CourseSessionModel {
        const id = bo.id;

        const title = this.hasCourse(bo) ? (bo.course?.name ?? "") : ((bo as ApiCourseDetailsType).name ?? "");
        const description = this.hasCourse(bo) ? (bo.course?.description ?? "") : ((bo as ApiCourseDetailsType).description ?? "");
        const imageSrc = this.hasCourse(bo) ? (bo.course?.thumbnail ?? "") : ((bo as ApiCourseDetailsType).thumbnail ?? "");

        const completion = (bo as any).completion_rate;
        const progress = typeof completion === "number" ? Math.round(completion * 100) : 0;


        const sessionMap = new Map<string, string>();
        const activitySessions: Array<{ id: string; activity_id: string; state: string; }> =
            (bo as any).activity_sessions ?? [];
        activitySessions.forEach(s => {
            if (s?.activity_id) sessionMap.set(s.activity_id, s.state ?? "not_started");
        });

        let stages: stageType[] = [];

        if (this.hasRootStages(bo)) {
            stages = bo.stages;
        } else if (this.hasCourse(bo) && Array.isArray(bo.course.stages)) {

            let stageOrder = 0;

            bo.course.stages.forEach((stage) => {
                stages.push({
                    stageOrder: stageOrder++,
                    name: stage.name,
                    expected_completion_rate: stage.expected_completion_rate,
                    grains: stage.grains.map((grain) => ({
                        id: grain.id,
                        courseSessionId: bo.id,
                        name: grain.name,
                        thumbnail: grain.thumbnail,
                        order: grain.order,
                    })),
                    locked: stage.locked,
                    activities: stage.activities.map((activity) => ({
                        id: activity.id,
                        courseSessionId: bo.id,
                        name: activity.name,
                        thumbnail: activity.thumbnail,
                        elements: activity.elements,
                        mandatory: activity.mandatory,
                        order: activity.order,
                        sessionCreatedBy: activity.sessionCreatedBy,
                        status: sessionMap.get(activity.id) ?? "not_started",
                    })),
                });
            });
        }

        return new CourseSessionModel({ id, title, description, imageSrc, stages, progress });
    }
}
